import { useEffect, useState } from 'react';

const BLANK = {
  name: '',
  contactName: '',
  email: '',
  phone: '',
  website: '',
  supplies: '',
  leadTimeDays: '',
  minimumOrder: '',
  rating: '',
  notes: '',
};

export default function VendorForm({
  initialVendor,
  submitLabel = initialVendor ? 'Update vendor' : 'Add vendor',
  onSubmit,
  onCancel,
}) {
  const [name, setName] = useState(BLANK.name);
  const [contactName, setContactName] = useState(BLANK.contactName);
  const [email, setEmail] = useState(BLANK.email);
  const [phone, setPhone] = useState(BLANK.phone);
  const [website, setWebsite] = useState(BLANK.website);
  const [supplies, setSupplies] = useState(BLANK.supplies);
  const [leadTimeDays, setLeadTimeDays] = useState(BLANK.leadTimeDays);
  const [minimumOrder, setMinimumOrder] = useState(BLANK.minimumOrder);
  const [rating, setRating] = useState(BLANK.rating);
  const [notes, setNotes] = useState(BLANK.notes);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (initialVendor) {
      setName(initialVendor.name);
      setContactName(initialVendor.contact_name || '');
      setEmail(initialVendor.email || '');
      setPhone(initialVendor.phone || '');
      setWebsite(initialVendor.website || '');
      setSupplies(initialVendor.supplies || '');
      setLeadTimeDays(initialVendor.lead_time_days != null ? String(initialVendor.lead_time_days) : '');
      setMinimumOrder(initialVendor.minimum_order || '');
      setRating(initialVendor.rating != null ? String(initialVendor.rating) : '');
      setNotes(initialVendor.notes || '');
    } else {
      setName(BLANK.name);
      setContactName(BLANK.contactName);
      setEmail(BLANK.email);
      setPhone(BLANK.phone);
      setWebsite(BLANK.website);
      setSupplies(BLANK.supplies);
      setLeadTimeDays(BLANK.leadTimeDays);
      setMinimumOrder(BLANK.minimumOrder);
      setRating(BLANK.rating);
      setNotes(BLANK.notes);
    }
    setError(null);
  }, [initialVendor]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Vendor name is required.');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit({
        name: name.trim(),
        contact_name: contactName || null,
        email: email || null,
        phone: phone || null,
        website: website.trim() || null,
        supplies: supplies || null,
        lead_time_days: leadTimeDays ? Number(leadTimeDays) : null,
        minimum_order: minimumOrder || null,
        rating: rating ? Number(rating) : null,
        notes: notes || null,
      });
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="vendor-form" onSubmit={handleSubmit}>
      <div className="vendor-form-grid">
        <label>
          Vendor name
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
        </label>

        <label>
          Contact person (optional)
          <input type="text" value={contactName} onChange={(e) => setContactName(e.target.value)} />
        </label>

        <label>
          Email (optional)
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>

        <label>
          Phone (optional)
          <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </label>

        <label>
          Website (optional)
          {/* Free text, not type="url" — the browser's url validation rejects
              a bare "shopname.com", which is how these usually get written. */}
          <input
            type="text"
            placeholder="shopname.com"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </label>

        <label className="vendor-form-wide">
          What they supply (optional)
          <input
            type="text"
            value={supplies}
            onChange={(e) => setSupplies(e.target.value)}
            placeholder="e.g. Glass jars, shea butter"
          />
        </label>

        <label>
          Lead time (days, optional)
          <input
            type="number"
            min="0"
            step="1"
            value={leadTimeDays}
            onChange={(e) => setLeadTimeDays(e.target.value)}
          />
        </label>

        <label>
          Minimum order (optional)
          <input
            type="text"
            value={minimumOrder}
            onChange={(e) => setMinimumOrder(e.target.value)}
            placeholder="e.g. 50 units, $500"
          />
        </label>

        <label>
          Rating (optional)
          <select value={rating} onChange={(e) => setRating(e.target.value)}>
            <option value="">—</option>
            {[1, 2, 3, 4, 5].map((n) => (
              <option key={n} value={n}>
                {n} {n === 1 ? 'star' : 'stars'}
              </option>
            ))}
          </select>
        </label>

        <label className="vendor-form-wide">
          Notes (optional)
          <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
      </div>

      {error && <p className="vendor-form-error">{error}</p>}

      <div className="vendor-form-actions">
        <button type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : submitLabel}
        </button>
        <button type="button" className="vendor-form-cancel" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
